import { useCallback, useRef, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useSnackBarContext } from '../contexts/SnackBarContext';

type UseApiActionOptions<TPayload extends Record<string, unknown>, TResult> = {
  action: (payload: TPayload) => Promise<TResult>;
  reloadUserData?: boolean;
  quietly?: boolean;
};

export default function useApiAction<
  TPayload extends Record<string, unknown> = Record<string, unknown>,
  TResult = unknown,
>({
  action,
  reloadUserData,
  quietly,
}: UseApiActionOptions<TPayload, TResult>): [
  (additionalPayload?: Partial<TPayload>) => Promise<TResult | undefined>,
  boolean,
] {
  const { currentUser, queryUserData } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const { addSnackBar } = useSnackBarContext();

  const invoke = useCallback(
    async (additionalPayload: Partial<TPayload> = {}) => {
      if (!currentUser) throw new Error('Please log in to perform this action');
      if (loadingRef.current) return;

      setLoading(true);
      loadingRef.current = true;

      try {
        const data = await action(additionalPayload as TPayload);
        setLoading(false);
        loadingRef.current = false;
        if (reloadUserData) void queryUserData();
        return data;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (!quietly) {
          addSnackBar({
            title: 'ERROR',
            message: `Error performing action. ${message}`,
            variant: 'danger',
            timeout: 4000,
          });
        }
        if (reloadUserData) void queryUserData();
        setLoading(false);
        loadingRef.current = false;
        throw err instanceof Error ? err : new Error(message);
      }
    },
    [action, currentUser, queryUserData, addSnackBar, quietly, reloadUserData]
  );

  return [invoke, loading];
}
